import React from 'react';

interface RankingsTabProps {
    selectedTeam: any;
    isDark: boolean;
    RankingTable: React.FC<{
        title: string;
        data: any[];
        columns: { key: string; label: string; format?: (value: any) => string; tooltip?: string }[];
        icon: string;
        color: string;
    }>;
}

const RankingsTab: React.FC<RankingsTabProps> = ({
    selectedTeam,
    isDark,
    RankingTable
}) => {
    // Calculate real rankings from actual team data
    const calculateRealRankings = () => {
        if (!selectedTeam?.recovery_data?.athletes_data) {
            return { recovery: [], sleep: [], strain: [], hrv: [] };
        }

        const recoveryRankings = selectedTeam.recovery_data.athletes_data.map((athlete: any) => {
            // Get real data from athlete's raw data - EXACT same as AthletesTab
            const recoveryData = athlete.recovery_data?.filter(((data: any) => data && data.recovery_score))
                .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()) || [];
            const sleepData = selectedTeam.sleep_data?.athletes_data
                .find((a: any) => a.athlete_name === athlete.athlete_name)?.sleep_data
                ?.filter(((data: any) => data && data.total_duration_hours))
                .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()) || [];
            const activityData = selectedTeam.activity_data?.athletes_data
                .find((a: any) => a.athlete_name === athlete.athlete_name)?.activity_data
                ?.filter(((data: any) => data && data.strain_score))
                .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()) || [];

            // Calculate real stats from actual data - EXACT same as AthletesTab
            const cleanRecoveryStats = {
                average: recoveryData.length > 0 ? recoveryData.reduce((sum: number, d: any) => sum + d.recovery_score, 0) / recoveryData.length : 0,
                latest: recoveryData.length > 0 ? recoveryData[recoveryData.length - 1].recovery_score : 0,
                dataPoints: recoveryData.length
            };

            const cleanSleepStats = {
                averageQuality: sleepData.length > 0 ? sleepData.reduce((sum: number, d: any) => sum + (d.sleep_quality_score || 0), 0) / sleepData.length : 0,
                averageDuration: sleepData.length > 0 ? sleepData.reduce((sum: number, d: any) => sum + d.total_duration_hours, 0) / sleepData.length : 0,
                latestQuality: sleepData.length > 0 ? (sleepData[sleepData.length - 1].sleep_quality_score || 0) : 0,
                latestDuration: sleepData.length > 0 ? sleepData[sleepData.length - 1].total_duration_hours : 0,
                dataPoints: sleepData.length
            };

            const cleanActivityStats = {
                averageStrain: activityData.length > 0 ? activityData.reduce((sum: number, d: any) => sum + d.strain_score, 0) / activityData.length : 0,
                latestStrain: activityData.length > 0 ? activityData[activityData.length - 1].strain_score : 0,
                dataPoints: activityData.length
            };

            // HRV calculations
            const hrvData = recoveryData.filter((d: any) => d.hrv_rmssd_milli).map((d: any) => d.hrv_rmssd_milli);
            const averageHRV = hrvData.length > 0 ? hrvData.reduce((a: number, b: number) => a + b, 0) / hrvData.length : 0;
            const latestHRV = hrvData.length > 0 ? hrvData[hrvData.length - 1] : 0;
            const hrvTrend = latestHRV - averageHRV;

            // Sleep efficiency calculation
            const efficiencies = sleepData.map((d: any) => d.sleep_efficiency || 0).filter((e: number) => e > 0);
            const averageEfficiency = efficiencies.length > 0 ? efficiencies.reduce((a: number, b: number) => a + b, 0) / efficiencies.length : 0;

            // Total strain calculation
            const totalStrain = activityData.length > 0 ? activityData.reduce((sum: number, d: any) => sum + d.strain_score, 0) : 0;

            return {
                name: athlete.athlete_name,
                // Recovery data - with proper rounding
                averageRecovery: Math.round(cleanRecoveryStats.average * 10) / 10,
                latestRecovery: Math.round(cleanRecoveryStats.latest * 10) / 10,
                recoveryTrend: Math.round((cleanRecoveryStats.latest - cleanRecoveryStats.average) * 10) / 10,
                dataPoints: cleanRecoveryStats.dataPoints,
                // HRV data - with proper rounding
                averageHRV: Math.round(averageHRV),
                latestHRV: Math.round(latestHRV),
                hrvTrend: Math.round(hrvTrend * 10) / 10,
                consistency: hrvData.length > 5 ? Math.round((1 - (Math.max(...hrvData) - Math.min(...hrvData)) / averageHRV) * 100) : 0,
                // Sleep data - with proper rounding
                averageQuality: Math.round(cleanSleepStats.averageQuality),
                averageDuration: Math.round(cleanSleepStats.averageDuration * 10) / 10,
                latestQuality: Math.round(cleanSleepStats.latestQuality),
                latestDuration: Math.round(cleanSleepStats.latestDuration * 10) / 10,
                averageEfficiency: Math.round(averageEfficiency * 100),
                sleepDataPoints: cleanSleepStats.dataPoints,
                // Activity data - with proper rounding
                averageStrain: Math.round(cleanActivityStats.averageStrain * 10) / 10,
                totalStrain: Math.round(totalStrain * 10) / 10,
                latestStrain: Math.round(cleanActivityStats.latestStrain * 10) / 10,
                workoutsCount: cleanActivityStats.dataPoints,
            };
        });

        return {
            recovery: [...recoveryRankings].sort((a: any, b: any) => b.averageRecovery - a.averageRecovery),
            sleep: [...recoveryRankings].sort((a: any, b: any) => b.averageDuration - a.averageDuration),
            strain: [...recoveryRankings].sort((a: any, b: any) => b.averageStrain - a.averageStrain),
            hrv: [...recoveryRankings].sort((a: any, b: any) => b.averageHRV - a.averageHRV),
        };
    };

    const realRankings = calculateRealRankings();
    return (
        <div className="space-y-6">
            {/* Trend Explanation */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-2">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">📈</span>
                    </div>
                    <h4 className="text-sm font-semibold theme-text-primary">Understanding Trends</h4>
                </div>
                <p className="text-xs theme-text-secondary leading-relaxed">
                    <strong>Trends</strong> show if an athlete is currently above or below their personal average:
                    <span className="text-green-600 font-medium"> +5.2</span> = performing above average (good trend)
                    <span className="text-red-600 font-medium"> -3.1</span> = performing below average (needs attention)
                    <span className="text-gray-600 font-medium"> 0.0</span> = right at their average
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <RankingTable
                    title="Recovery Rankings"
                    data={realRankings.recovery}
                    columns={[
                        { key: 'averageRecovery', label: 'Avg Recovery', format: (val) => val > 0 ? `${val.toFixed(1)}%` : 'N/A', tooltip: 'Average recovery score over the time period' },
                        { key: 'latestRecovery', label: 'Latest', format: (val) => val > 0 ? `${val.toFixed(1)}%` : 'N/A', tooltip: 'Most recent recovery score' },
                        { key: 'recoveryTrend', label: 'Trend', format: (val) => val !== 0 ? `${val > 0 ? '+' : ''}${val.toFixed(1)}` : '0.0', tooltip: 'Current vs average (+above, -below)' },
                        { key: 'dataPoints', label: 'Days', tooltip: 'Number of days with data' },
                    ]}
                    icon="💚"
                    color="from-blue-500 to-blue-600"
                />

                <RankingTable
                    title="HRV Rankings"
                    data={realRankings.hrv}
                    columns={[
                        { key: 'averageHRV', label: 'Avg HRV', format: (val) => val > 0 ? `${val.toFixed(0)}` : 'N/A', tooltip: 'Average heart rate variability. Higher = better recovery capacity. Individual baselines vary.' },
                        { key: 'latestHRV', label: 'Latest HRV', format: (val) => val > 0 ? `${val.toFixed(0)}` : 'N/A', tooltip: 'Most recent HRV reading. Compare to average to assess current recovery state.' },
                        { key: 'hrvTrend', label: 'Trend', format: (val) => val !== 0 ? `${val > 0 ? '+' : ''}${val.toFixed(1)}` : '0.0', tooltip: 'Current vs average. Positive = well recovered, negative = may need rest.' },
                        { key: 'consistency', label: 'Consistency', format: (val) => val > 0 ? `${val}%` : 'N/A', tooltip: 'HRV stability over time. Higher % = more consistent. Requires 6+ data points.' },
                    ]}
                    icon="🫀"
                    color="from-red-500 to-red-600"
                />

                <RankingTable
                    title="Sleep Rankings"
                    data={realRankings.sleep}
                    columns={[
                        { key: 'averageQuality', label: 'Avg Quality', format: (val) => val > 0 ? `${val}%` : 'N/A', tooltip: 'Average sleep quality score' },
                        { key: 'averageDuration', label: 'Avg Duration', format: (val) => val > 0 ? `${val}h` : 'N/A', tooltip: 'Average hours of sleep per night' },
                        { key: 'averageEfficiency', label: 'Efficiency', format: (val) => val > 0 ? `${val}%` : 'N/A', tooltip: 'Percentage of time in bed actually sleeping' },
                        { key: 'sleepDataPoints', label: 'Days', tooltip: 'Number of days with sleep data' },
                    ]}
                    icon="😴"
                    color="from-purple-500 to-purple-600"
                />

                <RankingTable
                    title="Strain Rankings"
                    data={realRankings.strain}
                    columns={[
                        { key: 'averageStrain', label: 'Avg Strain', format: (val) => val > 0 ? `${val.toFixed(1)}` : 'N/A', tooltip: 'Average daily cardiovascular strain' },
                        { key: 'totalStrain', label: 'Total', format: (val) => val > 0 ? `${val.toFixed(1)}` : 'N/A', tooltip: 'Cumulative strain over the period' },
                        { key: 'latestStrain', label: 'Latest', format: (val) => val > 0 ? `${val.toFixed(1)}` : 'N/A', tooltip: 'Most recent strain score' },
                        { key: 'workoutsCount', label: 'Workouts', tooltip: 'Number of recorded workouts' },
                    ]}
                    icon="💪"
                    color="from-emerald-500 to-emerald-600"
                />
            </div>
        </div>
    );
};

export default RankingsTab; 